import { Suspense, useId, useState, type ReactNode } from 'react'
import { useAnalytics } from '../../../analytics'
import { CFO_EVENTS, cfoDepthSurface, type CfoSurface } from './cfoTelemetry'

// CfoDepthSection — the calm "depth, one tap down" disclosure. The dense
// artifact (embedded legacy panel or imported workspace) is lazy-mounted only
// when the user opens it, and unmounted when collapsed — keeping the page calm
// and the heavy panel off the initial render.

export type CfoDepthSectionProps = {
  /** Section title (e.g. "The full statement"). */
  title: string
  /** Section meta line. */
  meta?: string
  /** Collapsed-state button label. */
  showLabel: string
  /** Expanded-state button label. */
  hideLabel?: string
  /** If true, the depth is mounted immediately (no disclosure). */
  defaultOpen?: boolean
  /** If true, mounts the depth region without rendering the disclosure button. */
  hideToggle?: boolean
  /** If true, keeps the accessible region label but hides the visible section title. */
  hideHeader?: boolean
  /** Lazy depth content factory — only invoked while open. */
  children: () => ReactNode
  /** Telemetry hook fired on toggle (in addition to the catalog event when `surface` is set). */
  onToggle?: (open: boolean) => void
  /** Page surface for `cfo_depth_toggle` telemetry. */
  surface?: CfoSurface
  workbookId?: string | null
}

export function CfoDepthSection({
  title,
  meta,
  showLabel,
  hideLabel = 'Hide detail',
  defaultOpen = false,
  hideToggle = false,
  hideHeader = false,
  children,
  onToggle,
  surface,
  workbookId = null,
}: CfoDepthSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const analytics = useAnalytics()
  const panelId = useId()
  return (
    <>
      {hideHeader ? null : (
        <div className="section-title rise">
          <h2 className="ff-guide">{title}</h2>
          {meta ? <span className="meta">{meta}</span> : null}
        </div>
      )}
      {hideToggle ? null : (
        <button
          type="button"
          className="btn"
          style={{ marginBottom: 'var(--s4)' }}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => {
            const next = !open
            setOpen(next)
            if (surface) {
              analytics.track({
                event: CFO_EVENTS.depthToggle,
                surface: cfoDepthSurface(surface),
                workbook_id: workbookId || null,
                props: { open: next, title },
              })
            }
            onToggle?.(next)
          }}
        >
          {open ? hideLabel : showLabel}
        </button>
      )}
      {open ? (
        <div className="cfo-shell-depth" id={panelId} role="region" aria-label={title}>
          <Suspense fallback={<div className="cfo-shell-depth-loading" role="status">Loading detail…</div>}>
            {children()}
          </Suspense>
        </div>
      ) : null}
    </>
  )
}

export default CfoDepthSection
